<template>
  <div class="sub-page">
    <!-- Top bar -->
    <div class="topbar">
      <button class="back" @click="$router.back()" aria-label="Назад">&#8592;</button>
      <h1>{{ iosSalesDisabled ? 'Премиум' : 'Выбор подписки' }}</h1>
    </div>

    <main class="wrap">

      <!-- Payment error banner -->
      <div v-if="paymentFailed" class="error-banner">
        <span>Оплата не прошла. Проверьте данные карты и попробуйте ещё раз.</span>
        <button @click="paymentFailed = false" class="error-close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <!-- Active subscription status -->
      <div v-if="authStore.user?.is_premium && isSubscriptionActive" class="active-sub">
        <div class="active-sub-header">
          <div class="active-dot" />
          <span>Подписка активна</span>
        </div>
        <p class="active-sub-detail">
          {{ activePlanLabel }} &middot; до {{ formatDate(authStore.user?.subscription_end) }}
        </p>
        <div v-if="authStore.user?.apple_managed">
          <p class="active-sub-note">{{ authStore.user?.has_rebill ? 'Автопродление включено' : 'Автопродление отключено' }} · оформлена в App Store</p>
          <button @click="openAppleSubscriptionSettings" class="cancel-link">Управление подпиской</button>
        </div>
        <div v-else-if="authStore.user?.has_rebill">
          <p class="active-sub-note">Автопродление включено</p>
          <button
            @click="cancelAutoRenewal"
            :disabled="cancelling"
            class="cancel-link"
          >
            {{ cancelling ? 'Отмена...' : 'Отменить автопродление' }}
          </button>
        </div>
        <p v-else class="active-sub-note">Автопродление отключено</p>
      </div>

      <!-- Hero -->
      <section class="hero">
        <h2>Дневник еды<br>без ручного ввода</h2>
      </section>

      <!-- Value cards -->
      <p class="eyebrow">{{ iosSalesDisabled ? 'Что даёт Премиум' : 'Что меняется с подпиской' }}</p>
      <section class="values">
        <div v-for="f in features" :key="f.title" class="vcard">
          <span class="vico">{{ f.icon }}</span>
          <div class="vtx">
            <h3>{{ f.title }}</h3>
            <p>{{ f.desc }}</p>
          </div>
        </div>
      </section>

      <!-- Bridge: у «Бережно» — про отсутствие давления; у остальных — настоящая
           цифра из журнала распознаваний (GET /api/app/recognition-stats) -->
      <section v-if="userType !== 'rpp'" class="bridge">
        <p><strong>{{ statLine }}</strong> Снимок — и блюдо в дневнике вместе с составом.</p>
      </section>
      <section v-else class="bridge">
        <p><strong>Здесь не нужно делать идеально.</strong> Пропустили день — просто продолжаете. Зоя не штрафует и не стыдит. Задача — не дисциплина через силу, а понимание, которое остаётся с вами.</p>
      </section>

      <!-- Вариант B (services/sales.js): в iOS-сборке продаж нет — ни тарифов,
           ни цен, ни кнопок оплаты, ни FAQ про способы оплаты (App Store 3.1.1).
           Показываем только суть: Премиум — свойство аккаунта. -->
      <div v-if="iosSalesDisabled" class="ios-account-note">
        <p>Премиум привязан к аккаунту. Если он у вас уже есть — войдите в этот
        аккаунт, и доступ включится автоматически.</p>
      </div>

      <template v-if="!iosSalesDisabled">
      <!-- Plans -->
      <p ref="plansRef" class="eyebrow">Выберите доступ</p>
      <section class="plans">
        <!-- Monthly -->
        <div
          class="plan"
          :class="{ selected: selectedPlan === '1_month' }"
          @click="selectedPlan = '1_month'"
          role="button"
          tabindex="0"
          :aria-pressed="selectedPlan === '1_month'"
          @keydown.enter.space.prevent="selectedPlan = '1_month'"
        >
          <span class="check" />
          <div class="plan-main">
            <div class="plan-title">Ежемесячно</div>
            <div class="plan-sub">Подписка с автопродлением. Отписаться можно в любой момент.</div>
          </div>
          <div class="plan-price">
            <div class="num">{{ prices.monthly || '…' }}</div>
            <div class="per">в месяц</div>
          </div>
        </div>

        <!-- Annual -->
        <div
          class="plan"
          :class="{ selected: selectedPlan === '12_months' }"
          @click="selectedPlan = '12_months'"
          role="button"
          tabindex="0"
          :aria-pressed="selectedPlan === '12_months'"
          @keydown.enter.space.prevent="selectedPlan = '12_months'"
        >
          <span v-if="prices.badgePercent" class="badge">Выгоднее на {{ prices.badgePercent }}%</span>
          <span class="check" />
          <div class="plan-main">
            <div class="plan-title">12 месяцев</div>
            <div class="plan-sub">{{ isIOS ? 'Годовая подписка с автопродлением. Отписаться можно в любой момент.' : 'Без автопродления — деньги спишутся один раз.' }}</div>
            <div v-if="prices.savingPerMonth" class="save-line">−{{ prices.savingPerMonth }}/мес</div>
          </div>
          <div class="plan-price">
            <div class="num">{{ prices.annualTotal || '…' }}</div>
            <div class="per">{{ isIOS ? 'в год' : 'за 12 месяцев' }}</div>
            <div v-if="prices.annualPerMonth" class="once">{{ prices.annualPerMonth }} в месяц</div>
          </div>
        </div>
      </section>

      <!-- Apple IAP (iOS only) -->
      <template v-if="isIOS">
        <button
          class="cta"
          @click="handleApplePayClick"
          :disabled="paying || !iapReady"
        >
          {{ paying ? 'Обработка...' : `Подписаться${iapPrice ? ' — ' + iapPrice : ''}` }}
        </button>
        <p v-if="iapPayError || iapInitError" class="pay-error">{{ iapPayError || `Не удалось получить цены из App Store (${iapInitError}). Попробуйте открыть экран позже.` }}</p>
        <p class="iap-terms">
          <strong>Зоя Премиум</strong> — {{ selectedPlan === '1_month' ? '1 месяц' : '12 месяцев' }}{{ iapPrice ? `, ${iapPrice}` : '' }}.
          Оплата спишется с аккаунта Apple ID при подтверждении покупки.
          Подписка продлевается автоматически, если не отменить её минимум за 24 часа
          до конца текущего периода. Стоимость продления та же.
          Управлять подпиской и отключить автопродление можно в настройках Apple ID.
        </p>

        <!-- App Store 3.1.1: восстановление покупок обязательно -->
        <button class="iap-restore" :disabled="restoring || !iapReady" @click="handleRestoreClick">
          {{ restoring ? 'Восстанавливаю…' : 'Восстановить покупки' }}
        </button>
        <p v-if="restoreMsg" class="iap-restore-msg">{{ restoreMsg }}</p>

        <!-- App Store 3.1.2: ссылки на соглашение и политику на экране покупки -->
        <p class="iap-legal">
          <a href="/legal/terms" @click.prevent="router.push('/legal/terms')">Пользовательское соглашение</a>
          <span aria-hidden="true"> · </span>
          <a href="/legal/privacy" @click.prevent="router.push('/legal/privacy')">Политика конфиденциальности</a>
        </p>
      </template>

      <!-- T-Bank + CloudPayments (Web / Android) -->
      <template v-else>
        <!-- Consent checkbox for monthly -->
        <label
          v-if="selectedPlan === '1_month'"
          class="consent"
          @click.prevent="consentChecked = !consentChecked"
        >
          <span class="consent-check" :class="{ checked: consentChecked }" />
          <span class="consent-text">
            Я соглашаюсь на автоматическое ежемесячное списание 390&nbsp;₽.
            Отменить подписку можно в любой момент в настройках профиля или написав в поддержку на <a href="mailto:m@zozhnik.ru" @click.stop>m@zozhnik.ru</a>.
          </span>
        </label>

        <!-- CTA -->
        <button
          class="cta"
          @click="handlePayClick"
          :disabled="paying || (selectedPlan === '1_month' && !consentChecked)"
        >
          {{ paying ? 'Перенаправление...' : 'Оплатить картой РФ' }}
        </button>
        <button
          v-if="!isNativePlatform"
          class="cta cta-secondary"
          @click="handleForeignPayClick"
          :disabled="paying || (selectedPlan === '1_month' && !consentChecked)"
        >
          Оплата картой иностранного банка
        </button>
      </template>
      <p v-if="payError" class="pay-error">{{ payError }}</p>

      <!-- Security -->
      <div class="secure">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--ink-faint)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>
        </svg>
        <span v-if="isIOS">Безопасная оплата через Apple. Управление подпиской — в настройках Apple ID.</span>
        <span v-else>Безопасная оплата. Сервис не имеет доступа к данным вашей карты. Все транзакции защищены технологией 3D-Secure и сертификатом SSL.</span>
      </div>

      <!-- FAQ (в нём цены и способы оплаты — под тем же флагом) -->
      <section class="faq">
        <p class="eyebrow">Остались вопросы?</p>
        <details v-for="(item, i) in faqItems" :key="i" class="qa">
          <summary>{{ item.q }}<span class="ic" /></summary>
          <div class="ans" v-html="item.a" />
        </details>
      </section>
      </template>

      <!-- Telegram -->
      <div class="tg">
        <svg width="22" height="22" viewBox="0 0 240 240" class="shrink-0" aria-hidden="true">
          <defs><linearGradient id="tgsub" x1="0.5" y1="0" x2="0.5" y2="1"><stop offset="0" stop-color="#2AABEE"/><stop offset="1" stop-color="#229ED9"/></linearGradient></defs>
          <circle cx="120" cy="120" r="120" fill="url(#tgsub)"/>
          <path d="M98 175c-3.9 0-3.2-1.5-4.6-5.2L82 132.2 170.4 78" fill="#C8DAEA"/>
          <path d="M98 175c3 0 4.3-1.4 6-3l16-15.6-20-12" fill="#A9C9DD"/>
          <path d="M100 144.4l48.4 35.7c5.5 3 9.5 1.5 10.9-5.1l19.7-92.8c2-8.1-3.1-11.7-8.4-9.3L55 117.5c-7.9 3.2-7.8 7.6-1.4 9.5l29.7 9.3L152 93.5c2.7-1.6 5.1-.7 3.1 1" fill="#FFF"/>
        </svg>
        <span>Следите за обновлениями в нашей <a href="https://t.me/+TaXmJC3u3wQ5OWQy" target="_blank" rel="noopener">телеграм-группе</a></span>
      </div>

    </main>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { useAuthStore } from '../store/auth.js'
import api from '../services/api.js'
import { track } from '../analytics.js'
import { initIAP, restorePurchases, getProductPrice, iapReady, iapError as iapInitError, isIOS, applePrices, buyApplePlan, manageSubscriptions } from '../services/iap.js'
import { iosSalesDisabled } from '../services/sales.js'
import { RF_PRICES, PENDING_PRICES } from '../services/prices.js'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()

const selectedPlan = ref('12_months')
watch(selectedPlan, () => { consentChecked.value = false })
const paying = ref(false)
const payError = ref(null)
const iapPayError = ref(null)
const restoring = ref(false)
const restoreMsg = ref(null)
const cancelling = ref(false)
const paymentFailed = ref(false)
const consentChecked = ref(false)
const isNativePlatform = Capacitor.isNativePlatform()

// IAP price (computed from App Store data)
const iapPrice = computed(() => getProductPrice(selectedPlan.value))
// Цены в карточках тарифов: в iOS — из App Store (Apple сверяет цифры на
// экране с ценой подписки), иначе рублёвые тарифы
const prices = computed(() => isIOS ? (applePrices.value || PENDING_PRICES) : RF_PRICES)

async function openAppleSubscriptionSettings() {
  await manageSubscriptions()
}

// ── Segment-specific content ─────────────────────────────
const userType = computed(() => authStore.user?.user_type || 'control')

// Что меняется с подпиской — пять коротких пунктов, от рутины к результату
// (решение владельца 26.10.2026: платят в момент, когда исчезает ручной ввод;
// дальше доверие к цифрам, Зоя, которая видит дневник, и план). Голос и
// повтор названы прямо: о голосе раньше не говорилось нигде.
const FEATURES = {
  rpp: [
    { icon: '📸', title: 'Фото или голос, без весов и подсчётов', desc: 'Сфотографировали или надиктовали — запись готова.' },
    { icon: '💚', title: 'Без цифр, если не хочется', desc: 'Шкала голода и сытости вместо калорий.' },
    { icon: '👩‍🏫', title: 'Зоя рядом, когда трудно', desc: '«Что делать, если на грани срыва» — без оценок и нотаций.' },
    { icon: '📊', title: 'Разбор недели без счёта грехов', desc: 'Ритм, регулярность, удачные блюда.' },
    { icon: '🎯', title: 'Помнит о вас', desc: 'Обстоятельства, ограничения, план — всё учитывается.' },
  ],
  control: [
    { icon: '📸', title: 'Фото или голос — и запись готова', desc: 'Зоя распознаёт блюдо, состав и вес. Вчерашнее повторяется одним нажатием.' },
    { icon: '🧪', title: 'Цифры, которым можно верить', desc: 'Составы из справочника Скурихина и базы USDA, нормы по формулам под ваш рост, вес и цель.' },
    { icon: '👩‍🏫', title: 'Зоя знает ваш дневник', desc: 'Спросите о любом блюде или о своём дне — ответ по вашим данным, а не советы «всем».' },
    { icon: '📊', title: 'Видно, что происходит', desc: 'Разбор дня и отчет за неделю: где недобор белка, где лишний сахар, что поправить.' },
    { icon: '🎯', title: 'План и память', desc: 'Зоя ведёт план к цели и помнит, что вы просили учитывать.' },
  ],
  training: [
    { icon: '📸', title: 'Фото или голос — БЖУ за 30 секунд', desc: 'Зоя распознаёт блюдо, вес и раскладку по белкам, жирам и углеводам.' },
    { icon: '🧪', title: 'Нормы под вашу задачу', desc: 'Набор, сушка или поддержание — а не «для худеющих».' },
    { icon: '💪', title: 'Белок под контролем', desc: 'Источники по приёмам, недобор виден в тот же день.' },
    { icon: '👩‍🏫', title: 'Зоя знает ваш дневник', desc: 'Вопрос по питанию под тренировки — ответ по вашим данным.' },
    { icon: '🎯', title: 'План и отчет за неделю', desc: 'Зоя ведёт план к цели и показывает неделю целиком.' },
  ],
}

const FAQ = {
  rpp: [
    { q: 'Не получается считать калории — всё время бросаю', a: 'Зоя не про подсчёт через силу. Сфотографировали еду — она посчитала сама. Пропуски не ломают прогресс: вы продолжаете с того места, где остановились. Вы можете получить выводы уже через день или неделю ведения дневника.' },
    { q: 'Нужно ли вести дневник постоянно?', a: 'Вести дневник питания всё время — не обязательно. Но честное ведение дневника даже 1 неделю показывает истинную картину вашего питания.' },
    { q: 'А если я сорвусь?', a: 'Срыв — это просто данные, а не провал. Зоя не показывает осуждающих сообщений и не ведёт «счёт грехов». Один день не отменяет всего пути.' },
    { q: 'Откуда данные о продуктах?', a: 'Из проверенных источников: справочника Скурихина (российские продукты), базы USDA (международная база Минсельхоза США) и базы производителей — всего более 680 000 продуктов и 450 000 штрихкодов, одна из самых больших баз на российском рынке. Не пользовательская база со случайными записями.' },
    { q: 'Будут ли списываться деньги потом?', a: 'При годовом доступе — нет, платёж разовый. При месячной подписке деньги списываются ежемесячно, отменить можно в любой момент.' },
    { q: 'Какие доступны способы оплаты?', a: 'Карты российских банков (МИР, VISA, Mastercard) и карты зарубежных банков (VISA, Mastercard).' },
    { q: 'Как обратиться в поддержку?', a: 'Напишите на почту <a href="mailto:m@zozhnik.ru">m@zozhnik.ru</a> или в группу <a href="https://t.me/+TaXmJC3u3wQ5OWQy" target="_blank" rel="noopener">«Зоя» в Telegram</a> — мы обязательно ответим.' },
  ],
  control: [
    { q: 'Насколько точно Зоя определяет вес и состав по фото?', a: 'Зоя распознаёт блюдо, раскладывает его на ингредиенты и оценивает вес по фото. Любую позицию можно проверить и поправить вручную — финальная цифра всегда под вашим контролем.' },
    { q: 'Откуда данные о продуктах?', a: 'Из проверенных источников: справочника Скурихина (российские продукты), базы USDA (международная база Минсельхоза США) и базы производителей — всего более 680 000 продуктов и 450 000 штрихкодов, одна из самых больших баз на российском рынке. Не пользовательская база со случайными записями. Новый продукт можно добавить самостоятельно, с последующей верификацией.' },
    { q: 'Как рассчитываются мои нормы?', a: 'По стандартным расчётным формулам с учётом ваших параметров, уровня физической активности и цели. При этом вы можете самостоятельно ввести нормы КБЖУ, а расчётные взять как дополнительный ориентир.' },
    { q: 'Будут ли списываться деньги потом?', a: 'При годовом доступе — нет, платёж разовый. При месячной подписке деньги списываются ежемесячно, отменить можно в любой момент.' },
    { q: 'Какие доступны способы оплаты?', a: 'Карты российских банков (МИР, VISA, Mastercard) и карты зарубежных банков (VISA, Mastercard).' },
    { q: 'Как обратиться в поддержку?', a: 'Напишите на почту <a href="mailto:m@zozhnik.ru">m@zozhnik.ru</a> или в группу <a href="https://t.me/+TaXmJC3u3wQ5OWQy" target="_blank" rel="noopener">«Зоя» в Telegram</a> — мы обязательно ответим.' },
  ],
  training: [
    { q: 'Можно ли настроить расчёт под набор массы?', a: 'Да. Зоя считает нормы под вашу цель — набор, поддержание или снижение, — а не только под дефицит калорий, как трекеры «для похудения».' },
    { q: 'Откуда данные о продуктах?', a: 'Из проверенных источников: справочника Скурихина (российские продукты), базы USDA (международная база Минсельхоза США) и базы производителей — всего более 680 000 продуктов и 450 000 штрихкодов, одна из самых больших баз на российском рынке. Не пользовательская база со случайными записями. Новый продукт можно добавить самостоятельно, с последующей верификацией.' },
    { q: 'Что если я ем одно и то же каждый день?', a: 'Повторяющиеся блюда записываются ещё быстрее — через функцию Избранного приём пищи добавляется в пару касаний.' },
    { q: 'Будут ли списываться деньги потом?', a: 'При годовом доступе — нет, платёж разовый. При месячной подписке деньги списываются ежемесячно, отменить можно в любой момент.' },
    { q: 'Какие доступны способы оплаты?', a: 'Карты российских банков (МИР, VISA, Mastercard) и карты зарубежных банков (VISA, Mastercard).' },
    { q: 'Как обратиться в поддержку?', a: 'Напишите на почту <a href="mailto:m@zozhnik.ru">m@zozhnik.ru</a> или в группу <a href="https://t.me/+TaXmJC3u3wQ5OWQy" target="_blank" rel="noopener">«Зоя» в Telegram</a> — мы обязательно ответим.' },
  ],
}

const features = computed(() => FEATURES[userType.value] || FEATURES.control)

// Настоящая цифра для плашки: сколько блюд распознано по фото за 30 дней
const photos30d = ref(null)
const statLine = computed(() => photos30d.value
  ? `${photos30d.value.toLocaleString('ru-RU')}+ блюд в месяц распознаются по фото.`
  : 'Тысячи блюд в месяц распознаются по фото.')
const plansRef = ref(null)
// В iOS подписки идут через App Store: ответы про списания и способы оплаты
// свои, рублёвых карт и разового платежа там нет
const FAQ_IOS = {
  'Будут ли списываться деньги потом?': 'Обе подписки продлеваются автоматически через App Store, пока вы не отмените их в настройках Apple ID. Отменить можно в любой момент — доступ сохранится до конца оплаченного периода.',
  'Какие доступны способы оплаты?': 'Оплата проходит через App Store со счёта вашего Apple ID — любым способом, который к нему привязан.',
}
const faqItems = computed(() => {
  const items = FAQ[userType.value] || FAQ.control
  return isIOS ? items.map(i => (FAQ_IOS[i.q] ? { ...i, a: FAQ_IOS[i.q] } : i)) : items
})

// ── Subscription status ──────────────────────────────────
const isSubscriptionActive = computed(() => {
  if (!authStore.user?.subscription_end) return false
  return new Date(authStore.user?.subscription_end) > new Date()
})

const activePlanLabel = computed(() => {
  const plan = authStore.user?.subscription_plan
  if (plan === '1_month') return 'Ежемесячная подписка'
  if (plan === '12_months') return 'Годовая подписка'
  return 'Подписка'
})

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
}

// ── Payment ──────────────────────────────────────────────
// Гостю сначала вход или регистрация: подписка должна лечь на настоящий
// аккаунт. С гостевого её не отменить в профиле, а при входе в старый
// аккаунт она пропадала (4.10.2026). Сервер гостю тоже откажет.
function needsAccount() {
  if (!authStore.isGuest) return false
  payError.value = 'Чтобы оформить подписку, войдите или зарегистрируйтесь — так она сохранится в вашем аккаунте'
  authStore.openAuthPopup()
  return true
}

function handlePayClick() {
  if (needsAccount()) return
  track('payment_initiated', { plan_id: selectedPlan.value, method: 'tbank' })
  initPayment()
}

function handleForeignPayClick() {
  if (needsAccount()) return
  track('payment_initiated', { plan_id: selectedPlan.value, method: 'cloudpayments' })
  initCpPayment()
}

async function initPayment() {
  paying.value = true
  payError.value = null
  try {
    const isNative = Capacitor.isNativePlatform()
    const { data } = await api.post('/payment/init', {
      planId: selectedPlan.value,
      platform: isNative ? 'native' : 'web',
    })
    if (data.paymentUrl) {
      if (isNative) {
        await Browser.open({ url: data.paymentUrl })
      } else {
        window.location.href = data.paymentUrl
      }
    }
  } catch (err) {
    payError.value = err.response?.data?.error || 'Ошибка создания платежа'
  } finally {
    paying.value = false
  }
}

let cpScriptLoaded = false
function loadCpScript() {
  return new Promise((resolve, reject) => {
    if (cpScriptLoaded || window.cp) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = 'https://widget.cloudpayments.ru/bundles/cloudpayments.js'
    script.onload = () => { cpScriptLoaded = true; resolve() }
    script.onerror = reject
    document.head.appendChild(script)
  })
}

async function initCpPayment() {
  paying.value = true
  payError.value = null
  try {
    await loadCpScript()
    const { data } = await api.post('/payment/cp-init', { planId: selectedPlan.value })
    const widgetOptions = {
      publicId: data.publicId,
      description: data.description,
      amount: data.amount,
      currency: data.currency,
      invoiceId: String(data.orderId),
      accountId: String(data.accountId),
    }
    if (data.recurrent) {
      widgetOptions.data = {
        CloudPayments: {
          recurrent: { interval: 'Month', period: 1 },
        },
      }
    }
    const widget = new window.cp.CloudPayments()
    widget.pay('charge', widgetOptions, {
      onSuccess() {
        window.location.href = '/profile/settings?payment=success'
      },
      onFail() {
        paymentFailed.value = true
        paying.value = false
      },
      onComplete() {
        paying.value = false
      },
    })
  } catch (err) {
    payError.value = err.response?.data?.error || 'Ошибка создания платежа'
    paying.value = false
  }
}

// Подписка App Store уже на аккаунте — по данным сервера (вебхук Apple или
// проверенный чек). Нужна покупке и восстановлению как второй путь
// подтверждения: чек с телефона в песочнице бывает недоступен.
async function appleSubscriptionActive() {
  await authStore.checkAuthStatus()
  const u = authStore.user
  return !!(u?.apple_managed && u?.subscription_end && new Date(u.subscription_end) > new Date())
}
async function waitAppleSubscription(tries = 6) {
  for (let i = 0; i < tries; i++) {
    if (await appleSubscriptionActive().catch(() => false)) return true
    await new Promise((r) => setTimeout(r, 2500))
  }
  return false
}

async function handleApplePayClick() {
  if (needsAccount()) return
  paying.value = true
  iapPayError.value = null
  track('payment_initiated', { plan_id: selectedPlan.value, method: 'apple_iap' })
  try {
    // Вход через попап мог заменить гостевой id, пока пейвол открыт.
    await initIAP(authStore.user?.id)
    const res = await buyApplePlan(selectedPlan.value, { refreshUser: () => authStore.checkAuthStatus(), getUser: () => authStore.user })
    if (res.status === 'activated') {
      router.push('/profile/settings?payment=success')
    } else if (res.status === 'scheduled') {
      restoreMsg.value = `App Store принял смену тарифа: ${selectedPlan.value === '12_months' ? 'годовая подписка' : 'месячная подписка'} начнётся${res.startsAt ? ` ${formatDate(res.startsAt)}` : ''}, когда закончится текущий период. До этого действует нынешняя.`
    } else {
      iapPayError.value = 'Оплата прошла, но подтверждение от App Store задерживается. Подписка включится сама в течение нескольких минут — откройте этот экран позже или нажмите «Восстановить покупки».'
    }
  } catch (err) {
    // User cancelled is not an error
    if (err.code === 'E_USER_CANCELLED' || err.message?.includes('cancel')) {
      // silent
    } else {
      iapPayError.value = err.message || 'Ошибка оплаты'
    }
  } finally {
    paying.value = false
  }
}

async function handleRestoreClick() {
  if (needsAccount()) return
  restoring.value = true
  restoreMsg.value = null
  track('iap_restore_started')
  try {
    await initIAP(authStore.user?.id)
    await restorePurchases()
    // Чеки уходят на /apple-verify через store.validator, а подписку может
    // включить и уведомление Apple — ждём, пока сервер её покажет
    await waitAppleSubscription()
    if (authStore.user?.is_premium) {
      router.push('/profile/settings?payment=success')
    } else {
      restoreMsg.value = 'Покупок для восстановления не нашлось. Проверьте, что вошли в тот же Apple ID, с которого оформляли подписку.'
    }
  } catch (err) {
    restoreMsg.value = err.message || 'Не удалось восстановить покупки'
  } finally {
    restoring.value = false
  }
}

async function cancelAutoRenewal() {
  cancelling.value = true
  try {
    await api.post('/payment/cancel')
    await authStore.checkAuthStatus()
  } catch (err) {
    console.error('Cancel error:', err)
  } finally {
    cancelling.value = false
  }
}

onMounted(async () => {
  window.scrollTo(0, 0)
  track('paywall_opened', { from: route.query.from || null })
  api.get('/app/recognition-stats').then(({ data }) => { photos30d.value = data?.photos_30d || null }).catch(() => {})
  // С первого пейвола (FirstPaywall.vue) — сразу к тарифам: аргументы человек только что прочитал
  if (route.query.from === 'paywall') {
    setTimeout(() => {
      const el = plansRef.value
      if (!el) return
      // Под закреплённой шапкой: заголовок «Выберите доступ» должен остаться виден
      const headerBottom = el.closest('.sub-page')?.querySelector('.topbar')?.getBoundingClientRect().bottom || 72
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - headerBottom - 16, behavior: 'smooth' })
    }, 150)
  }

  // Initialize Apple IAP on iOS
  if (isIOS) {
    await initIAP(authStore.user?.id)
  }

  if (route.query.payment === 'error') {
    sessionStorage.setItem('zoya-payment-error', '1')
    window.location.replace('/subscription')
    return
  }
  if (sessionStorage.getItem('zoya-payment-error')) {
    sessionStorage.removeItem('zoya-payment-error')
    paymentFailed.value = true
  }
})
</script>

<style scoped>
.ios-account-note {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 14px 16px;
  margin: 18px 0 6px;
  font-size: var(--fs-14);
  line-height: 1.5;
  color: var(--ink-soft);
}

.sub-page {
  --bg: #FAF3E8;
  --surface: #FBF8F3;
  --surface-2: #FFFFFF;
  --ink: #41291A;
  --ink-soft: rgb(65 41 26 / 0.7);
  --ink-faint: rgb(65 41 26 / 0.7);
  --brand: #41291A;
  --brand-deep: #41291A;
  --accent: #8B6846;
  --accent-soft: #E7D9C6;
  --line: #E8E2D9;
  --ok: #5C6B3A;  /* было #7A8B6A: 3.67:1 на белом, ниже AA; теперь 5.8 */
  --radius: 20px;
  --font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;

  min-height: 100vh;
  background: var(--bg);
  color: var(--ink);
  font-family: var(--font);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}

/* top bar */
.topbar {
  position: sticky;
  top: var(--safe-top, 0px);
  z-index: 10;
  background: var(--bg);
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px 14px;
  max-width: 430px;
  margin: 0 auto;
}
/* The app already offsets the initial flow; keep the sticky header below
   the status bar while scrolling and cover content passing behind it. */
.topbar::before {
  content: "";
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  height: var(--safe-top, 0px);
  background: var(--bg);
}
.back {
  font-size: var(--fs-22);
  color: var(--ink);
  line-height: 1;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.topbar h1 {
  font-size: var(--fs-17);
  font-weight: 600;
  letter-spacing: 0.01em;
}

/* wrap */
.wrap {
  max-width: 430px;
  margin: 0 auto;
  padding: 0 20px 120px;
}

/* error banner */
.error-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 12px;
  background: #fee2e2;
  color: #991b1b;
  font-size: var(--fs-14);
  font-weight: 500;
}
.error-close {
  flex-shrink: 0;
  margin-top: 2px;
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
}

/* active subscription */
.active-sub {
  margin-bottom: 16px;
  padding: 16px;
  border-radius: var(--radius);
  background: var(--surface-2);
  border: 1px solid var(--line);
}
.active-sub-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.active-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ok);
}
.active-sub-header span {
  font-size: var(--fs-14);
  font-weight: 600;
  color: var(--brand-deep);
}
.active-sub-detail {
  font-size: var(--fs-13);
  color: var(--ink-soft);
  margin-bottom: 12px;
}
.active-sub-note {
  font-size: var(--fs-12);
  color: var(--ink-faint);
  margin-bottom: 8px;
}
.cancel-link {
  font-size: var(--fs-12);
  color: var(--error);
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.cancel-link:disabled {
  opacity: 0.5;
}

/* hero */
.hero {
  padding: 14px 4px 26px;
}
.hero h2 {
  font-size: 27px;
  line-height: 1.18;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--brand-deep);
}

/* eyebrow */
.eyebrow {
  font-size: var(--fs-12);
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--accent);
  margin: 0 4px 14px;
}

/* value cards */
.values {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 34px;
}
.vcard {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 18px 18px 19px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
}
.vico {
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  border-radius: 13px;
  background: var(--accent-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
  line-height: 1;
}
.vtx {
  flex: 1 1 auto;
}
.vtx h3 {
  font-size: var(--fs-16);
  font-weight: 650;
  color: var(--brand-deep);
  margin-bottom: 6px;
}
.vtx p {
  font-size: var(--fs-14);
  color: var(--ink-soft);
  line-height: 1.5;
}

/* bridge */
.bridge {
  background: var(--brand-deep);
  color: #F1E8DC;
  border-radius: var(--radius);
  padding: 24px 22px;
  margin-bottom: 36px;
}
.bridge p {
  font-size: var(--fs-16);
  line-height: 1.55;
}
.bridge strong {
  color: #fff;
  font-weight: 650;
}

/* plans */
.plans {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 18px;
}
.plan {
  position: relative;
  background: var(--surface-2);
  border: 1.5px solid var(--line);
  border-radius: var(--radius);
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.plan.selected {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(185, 138, 94, 0.14);
}
.check {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  margin-top: 2px;
  border-radius: 7px;
  border: 1.5px solid var(--ink-faint);
  background: #fff;
  position: relative;
  transition: 0.15s;
}
.plan.selected .check {
  background: var(--accent);
  border-color: var(--accent);
}
.plan.selected .check::after {
  content: "";
  position: absolute;
  left: 7px;
  top: 3px;
  width: 5px;
  height: 10px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.plan-main {
  flex: 1 1 auto;
}
.plan-title {
  font-size: var(--fs-16);
  font-weight: 650;
  color: var(--brand-deep);
}
.plan-sub {
  font-size: var(--fs-13);
  color: var(--ink-soft);
  margin-top: 3px;
}
.plan-price {
  min-width: 72px;
  padding-left: 8px;
  text-align: right;
  flex: 0 0 auto;
}
.plan-price .num {
  font-size: 21px;
  font-weight: 700;
  color: var(--brand-deep);
  white-space: nowrap;
}
.plan-price .per {
  font-size: var(--fs-12);
  color: var(--ink-soft);
}
.plan-price .once {
  font-size: var(--fs-11);
  color: var(--ink-faint);
  margin-top: 4px;
}
.badge {
  position: absolute;
  top: -11px;
  right: 18px;
  background: var(--accent);
  color: #fff;
  font-size: var(--fs-11);
  font-weight: 650;
  letter-spacing: 0.02em;
  padding: 4px 11px;
  border-radius: 20px;
}
.save-line {
  display: inline-block;
  margin-top: 9px;
  background: rgba(122, 139, 106, 0.14);
  color: var(--ok);
  font-size: var(--fs-12);
  font-weight: 700;
  letter-spacing: 0.01em;
  padding: 4px 10px;
  border-radius: 30px;
}

/* consent */
.consent {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 4px;
  cursor: pointer;
}
.consent-check {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  margin-top: 2px;
  border-radius: 7px;
  border: 1.5px solid var(--ink-faint);
  background: #fff;
  position: relative;
  transition: 0.15s;
}
.consent-check.checked {
  background: var(--accent);
  border-color: var(--accent);
}
.consent-check.checked::after {
  content: "";
  position: absolute;
  left: 7px;
  top: 3px;
  width: 5px;
  height: 10px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.consent-text {
  font-size: var(--fs-13);
  color: var(--ink-soft);
  line-height: 1.5;
}
.consent-text a {
  color: var(--accent);
  font-weight: 600;
  text-decoration: underline;
}

/* cta */
.cta {
  width: 100%;
  border: none;
  cursor: pointer;
  background: var(--brand-deep);
  color: #fff;
  font-size: var(--fs-16);
  font-weight: 600;
  font-family: var(--font);
  padding: 17px;
  border-radius: 16px;
  margin-top: 18px;
  transition: transform 0.08s ease, background 0.15s ease;
}
.cta.cta-secondary {
  background: var(--accent-soft);
  color: var(--brand-deep);
  margin-top: 10px;
  font-size: var(--fs-14);
}
.cta.cta-secondary:active:not(:disabled) {
  background: #d9c8ae;
}
.cta:active:not(:disabled) {
  transform: scale(0.99);
}
.cta:disabled {
  opacity: 0.5;
  cursor: default;
}
.pay-error {
  font-size: var(--fs-13);
  color: var(--error);
  text-align: center;
  margin-top: 8px;
}
.iap-terms {
  font-size: var(--fs-12);
  color: var(--ink-faint);
  text-align: center;
  margin-top: 10px;
  line-height: 1.45;
}

.iap-restore {
  display: block;
  width: 100%;
  margin-top: 12px;
  padding: 11px 0;
  background: none;
  border: none;
  font-size: var(--fs-14);
  font-weight: 600;
  color: var(--ink);
  text-decoration: underline;
  cursor: pointer;
}
.iap-restore:disabled {
  opacity: 0.45;
  cursor: default;
}
.iap-restore-msg {
  font-size: var(--fs-12);
  color: var(--ink-faint);
  text-align: center;
  line-height: 1.45;
  margin-top: 2px;
}
.iap-legal {
  font-size: var(--fs-12);
  color: var(--ink-faint);
  text-align: center;
  margin-top: 10px;
}
.iap-legal a {
  color: var(--ink-faint);
  text-decoration: underline;
}

/* security */
.secure {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  font-size: var(--fs-12);
  color: var(--ink-faint);
  margin: 14px 4px 0;
  line-height: 1.45;
}
.secure svg {
  flex: 0 0 auto;
  margin-top: 1px;
}

/* faq */
.faq {
  margin-top: 42px;
}
.faq > .eyebrow {
  margin-bottom: 18px;
}
.qa {
  border-bottom: 1px solid var(--line);
}
.qa:first-of-type {
  border-top: 1px solid var(--line);
}
.qa summary {
  list-style: none;
  cursor: pointer;
  padding: 17px 4px;
  font-size: var(--fs-15);
  font-weight: 600;
  color: var(--brand-deep);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
}
.qa summary::-webkit-details-marker {
  display: none;
}
.qa summary .ic {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  position: relative;
  transition: 0.2s;
}
.qa summary .ic::before,
.qa summary .ic::after {
  content: "";
  position: absolute;
  background: var(--accent);
  border-radius: 2px;
}
.qa summary .ic::before {
  left: 8px;
  top: 2px;
  width: 2px;
  height: 14px;
}
.qa summary .ic::after {
  left: 2px;
  top: 8px;
  width: 14px;
  height: 2px;
}
.qa[open] summary .ic::before {
  transform: scaleY(0);
}
.qa[open] summary .ic {
  transform: rotate(180deg);
}
.qa .ans {
  padding: 0 4px 18px;
  font-size: var(--fs-14);
  color: var(--ink-soft);
  max-width: 40ch;
  line-height: 1.5;
}
.qa .ans :deep(a) {
  color: var(--accent);
  font-weight: 600;
  text-decoration: none;
}

/* telegram */
.tg {
  margin-top: 34px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: var(--fs-14);
  color: var(--ink-soft);
}
.tg .dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
}
.tg a {
  color: var(--accent);
  font-weight: 650;
  text-decoration: none;
}

@media (prefers-reduced-motion: reduce) {
  * {
    transition: none !important;
  }
}
</style>
